import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiError } from './api-error';

interface RequestOptions {
  params?: HttpParams;
  retryable?: boolean;
}

const MAX_RETRY_COUNT = 2;
const RETRY_BACKOFF_MS = 300;
const TRANSIENT_HTTP_STATUSES = [0, 408, 429, 502, 503, 504];
const DEFAULT_ERROR_MESSAGE = 'An unexpected API error occurred.';

@Injectable({
  providedIn: 'root',
})
export class HttpApiService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  get<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    const retryable = options?.retryable ?? true;
    const request$ = this.http.get<T>(`${this.baseUrl}${endpoint}`, { params: options?.params });

    if (retryable) {
      return request$.pipe(
        retry({
          count: MAX_RETRY_COUNT,
          delay: (error, retryCount) => {
            if (error instanceof HttpErrorResponse && this.isTransient(error.status)) {
              const backoffMs = retryCount * RETRY_BACKOFF_MS;
              return timer(backoffMs);
            }
            return throwError(() => error);
          },
        }),
        catchError(this.handleError),
      );
    }

    return request$.pipe(catchError(this.handleError));
  }

  post<T>(endpoint: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}${endpoint}`, body, { params: options?.params })
      .pipe(catchError(this.handleError));
  }

  postBlob(
    endpoint: string,
    body: unknown,
    options?: RequestOptions,
  ): Observable<HttpResponse<Blob>> {
    return this.http
      .post(`${this.baseUrl}${endpoint}`, body, {
        params: options?.params,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(catchError(this.handleBlobError));
  }

  put<T>(endpoint: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}${endpoint}`, body, { params: options?.params })
      .pipe(catchError(this.handleError));
  }

  patch<T>(endpoint: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http
      .patch<T>(`${this.baseUrl}${endpoint}`, body, { params: options?.params })
      .pipe(catchError(this.handleError));
  }

  delete<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.http
      .delete<T>(`${this.baseUrl}${endpoint}`, { params: options?.params })
      .pipe(catchError(this.handleError));
  }

  private isTransient(status: number): boolean {
    return TRANSIENT_HTTP_STATUSES.includes(status);
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    const isObjectPayload = typeof error.error === 'object' && error.error !== null;
    const serverMessage = isObjectPayload ? (error.error.error as string) : null;
    const fieldErrors = isObjectPayload
      ? this.extractFieldErrors(error.error as Record<string, unknown>)
      : undefined;
    const code = isObjectPayload ? (error.error.code as string | undefined) : undefined;

    const message = serverMessage || error.message || DEFAULT_ERROR_MESSAGE;

    return throwError(() => new ApiError(message, error.status, error.url, fieldErrors, code));
  };

  private extractFieldErrors(
    payload: Record<string, unknown>,
  ): Record<string, string[]> | undefined {
    if (payload['field_errors'] && typeof payload['field_errors'] === 'object') {
      return payload['field_errors'] as Record<string, string[]>;
    }
    const extracted: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(payload)) {
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        extracted[key] = value;
      }
    }
    return Object.keys(extracted).length > 0 ? extracted : undefined;
  }

  private handleBlobError = (error: HttpErrorResponse): Observable<never> => {
    if (error.error instanceof Blob) {
      return from(error.error.text()).pipe(
        switchMap((text) => {
          let parsedError: unknown;
          try {
            parsedError = JSON.parse(text);
          } catch {
            parsedError = null;
          }

          const isParsedObject = typeof parsedError === 'object' && parsedError !== null;
          const parsedObj = isParsedObject ? (parsedError as Record<string, unknown>) : null;
          const hasErrorMessage = parsedObj !== null && 'error' in parsedObj;
          const parsedMessage = hasErrorMessage ? String(parsedObj['error']) : null;

          const message = parsedMessage || error.message || DEFAULT_ERROR_MESSAGE;

          return throwError(() => new ApiError(message, error.status, error.url));
        }),
      );
    }
    return this.handleError(error);
  };
}
